import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/extension'
import { INSERT_CHECK_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from '@lexical/list'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $patchStyleText } from '@lexical/selection'
import { INSERT_TABLE_COMMAND } from '@lexical/table'
import { Dropdown, Select } from 'antd'
import {
  $isRangeSelection,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
  type ElementFormatType
} from 'lexical'
import {
  AlignLeft,
  Highlighter,
  IndentDecrease,
  IndentIncrease,
  Link2,
  List,
  ListChecks,
  ListOrdered,
  Minus,
  Palette,
  Redo2,
  RemoveFormatting,
  Table2,
  Undo2
} from 'lucide-react'
import {
  ALIGNMENT_OPTIONS,
  FONT_FAMILY_OPTIONS,
  FONT_SIZE_OPTIONS,
  TABLE_OPTIONS
} from '@/features/contract/const/contract-lexical-toolbar.tsx'
import { BLOCK_OPTIONS, FORMAT_BUTTONS } from '@/features/contract/const/toolbar.tsx'
import { LinkDialog } from '@/features/contract/contract-link-dialog.tsx'
import { ToolbarButton, ToolbarDivider } from '@/features/contract/contract-toolbar-button.tsx'
import { useToolbarState } from '@/features/contract/hooks/use-toolbar-state.ts'
import { clearFormatting, runInSelection, setBlockType } from '@/features/contract/utils/toolbar.ts'

export function ContractLexicalToolbar({ onRequestLink }: { onRequestLink?: () => void }) {
  const [editor] = useLexicalComposerContext()
  const state = useToolbarState(editor)

  return (
    <div className="contract-editor-toolbar border-border bg-surface-muted shrink-0 overflow-x-auto border-b px-3 py-1">
      <div className="flex min-w-max flex-nowrap items-center gap-0.5">
        <ToolbarButton
          icon={<Undo2 aria-hidden size={14} />}
          label="撤销"
          onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
        />
        <ToolbarButton
          icon={<Redo2 aria-hidden size={14} />}
          label="重做"
          onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
        />
        <ToolbarDivider />
        <Select
          aria-label="段落样式"
          className="w-24"
          onChange={(value) => setBlockType(editor, value)}
          options={BLOCK_OPTIONS}
          size="small"
          value={state.blockType}
        />
        <Select
          aria-label="字体"
          className="hidden w-28 md:block"
          onChange={(value) =>
            runInSelection(
              editor,
              (selection) => $isRangeSelection(selection) && $patchStyleText(selection, { 'font-family': value })
            )
          }
          options={FONT_FAMILY_OPTIONS}
          size="small"
          value={state.fontFamily}
        />
        <Select
          aria-label="字号"
          className="w-16"
          onChange={(value) =>
            runInSelection(
              editor,
              (selection) => $isRangeSelection(selection) && $patchStyleText(selection, { 'font-size': value })
            )
          }
          options={FONT_SIZE_OPTIONS}
          size="small"
          value={state.fontSize}
        />
        <ToolbarDivider />
        {FORMAT_BUTTONS.map((button) => (
          <ToolbarButton
            active={state.formats[button.format]}
            icon={button.icon}
            key={button.format}
            label={button.label}
            onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, button.format)}
          />
        ))}
        <ToolbarDivider />
        <ToolbarButton
          icon={<Palette aria-hidden size={14} />}
          label="文字颜色"
          onClick={() => document.getElementById('contract-editor-text-color')?.click()}
        />
        <input
          aria-label="文字颜色"
          className="sr-only"
          id="contract-editor-text-color"
          onChange={(event) =>
            runInSelection(
              editor,
              (selection) => $isRangeSelection(selection) && $patchStyleText(selection, { color: event.target.value })
            )
          }
          type="color"
          defaultValue="#1f2937"
        />
        <ToolbarButton
          icon={<Highlighter aria-hidden size={14} />}
          label="背景颜色"
          onClick={() => document.getElementById('contract-editor-highlight-color')?.click()}
        />
        <input
          aria-label="背景颜色"
          className="sr-only"
          id="contract-editor-highlight-color"
          onChange={(event) =>
            runInSelection(
              editor,
              (selection) =>
                $isRangeSelection(selection) && $patchStyleText(selection, { 'background-color': event.target.value })
            )
          }
          type="color"
          defaultValue="#fef08a"
        />
        <ToolbarDivider />
        <ToolbarButton
          icon={<List aria-hidden size={14} />}
          label="项目符号列表"
          onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)}
        />
        <ToolbarButton
          icon={<ListOrdered aria-hidden size={14} />}
          label="编号列表"
          onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)}
        />
        <ToolbarButton
          icon={<ListChecks aria-hidden size={14} />}
          label="待办列表"
          onClick={() => editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined)}
        />
        <ToolbarButton
          icon={<IndentDecrease aria-hidden size={14} />}
          label="减少缩进"
          onClick={() => editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined)}
        />
        <ToolbarButton
          icon={<IndentIncrease aria-hidden size={14} />}
          label="增加缩进"
          onClick={() => editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined)}
        />
        <Dropdown
          menu={{
            items: ALIGNMENT_OPTIONS.map((option) => ({ icon: option.icon, key: option.value, label: option.label })),
            onClick: ({ key }) => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, key as Exclude<ElementFormatType, ''>)
          }}
          trigger={['click']}
        >
          <span>
            <ToolbarButton
              active={state.alignment !== ''}
              icon={
                ALIGNMENT_OPTIONS.find((option) => option.value === state.alignment)?.icon ?? (
                  <AlignLeft aria-hidden size={14} />
                )
              }
              label="对齐方式"
              onClick={() => undefined}
            />
          </span>
        </Dropdown>
        <ToolbarDivider />
        <ToolbarButton icon={<Link2 aria-hidden size={14} />} label="插入链接" onClick={() => onRequestLink?.()} />
        <Dropdown
          menu={{
            items: TABLE_OPTIONS.map((option) => ({ key: option.key, label: option.label })),
            onClick: ({ key }) => {
              const option = TABLE_OPTIONS.find((item) => item.key === key)
              if (option) {
                editor.dispatchCommand(INSERT_TABLE_COMMAND, { columns: option.columns, rows: option.rows })
              }
            }
          }}
          trigger={['click']}
        >
          <span>
            <ToolbarButton icon={<Table2 aria-hidden size={14} />} label="插入表格" onClick={() => undefined} />
          </span>
        </Dropdown>
        <ToolbarButton
          icon={<Minus aria-hidden size={14} />}
          label="分割线"
          onClick={() => editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined)}
        />
        <ToolbarButton
          icon={<RemoveFormatting aria-hidden size={14} />}
          label="清除格式"
          onClick={() => clearFormatting(editor)}
        />
      </div>
    </div>
  )
}

export { LinkDialog }
